import React from 'react'
import { useData } from 'vike-react/useData'

import { Section } from '../../../components/Section'
import { SectionFAQ } from '../../../components/SectionFAQ'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SectionTemplates } from '../../../components/SectionTemplates'
import { SlitePage } from '../../../components/SlitePage'
import { useRoutes } from '../../../routes'
import { getTemplateColorFromRemote } from '../../templates/constants'
import type { Data, SolutionsPageData } from './+data'
import { SectionSolutionsAdvantage } from './SectionSolutionsAdvantage'
import { SectionSolutionsHero } from './SectionSolutionsHero'
import { UseCaseDetail } from './UseCaseDetail'

const SOLUTION_IT_OPERATIONS = '5f52caea-f6f0-4f55-92fd-62da71870706'
const SOLUTION_PRODUCT_ENGINEERING = 'dd4b731c-ceb5-4ab8-95ea-344764603675'
const SOLUTION_HR = 'f97ce0a9-9e91-405f-a0ab-51537b0381ed'
const SOLUTION_CUSTOMER_SUPPORT = '7a5dfcf9-325a-4f45-8bb4-69bdb8d2604a'
const SOLUTION_SALES = 'c68312e8-bcfd-4a08-991c-fc74f80a700a'

export default function SolutionsShowPage() {
  const getColors = (page: SolutionsPageData) => {
    if (page._id.includes(SOLUTION_IT_OPERATIONS)) {
      // IT & Operations
      return 'slite-blue-500'
    } else if (page._id.includes(SOLUTION_PRODUCT_ENGINEERING)) {
      // Product & Engineering
      return 'moon-dust-darkest'
    } else if (page._id.includes(SOLUTION_HR)) {
      // HR
      return 'slite-yellow-500'
    } else if (
      page._id.includes(SOLUTION_CUSTOMER_SUPPORT) ||
      page._id.includes(SOLUTION_SALES)
    ) {
      // Customer Support or Sales
      return 'slite-pink-500'
    } else {
      // Knowledge base
      return 'slite-blue-500'
    }
  }

  const data = useData<Data>()
  if (data.kind === 'useCase') {
    return <UseCaseDetail useCase={data.useCase} />
  }
  return <SolutionDetail page={data.page} brandColor={getColors(data.page)} />
}

function SolutionDetail({
  page,
  brandColor,
}: {
  page: SolutionsPageData
  brandColor: string
}) {
  const { title, description } = page
  const routes = useRoutes()
  const pageUrl = routes.solutions.detail({ slug: page.slug.current })

  return (
    <SlitePage
      title={page.openGraphTitle}
      description={page.metaDescription ?? page.description}
      ogImage={page.openGraphImage?.url}
      ogTitle={page.openGraphTitle}
      ogDescription={page.openGraphDescription}
      canonicalUrl={pageUrl}
      background="moon-dust-lighter"
    >
      <SectionSolutionsHero
        title={title}
        description={description}
        teamName={page.name}
        imageUrl={page.image.url}
        illustrationUrl={page.illustration?.url || null}
        illustrationSmall={page.illustrationSmall?.url || null}
        brandColor={brandColor}
      />
      <Section
        background="moon-dust-lighter"
        containerVerticalPadding={80}
        innerStyle={{ display: 'flex', flexDirection: 'column', gap: 80 }}
      >
        {page.advantages.map((advantage, index) => (
          <SectionSolutionsAdvantage
            key={index}
            index={index}
            title={advantage.title}
            description={advantage.description}
            imageUrl={advantage.image.url}
            name={advantage.name}
            quote={
              advantage.quote
                ? {
                    name: advantage.quote.name,
                    title: `${advantage.quote.title} at ${advantage.quote.company}`,
                    testimonial: advantage.quote.testimonial,
                    pictureUrl: advantage.quote.picture.url,
                  }
                : null
            }
            brandColor={brandColor}
          />
        ))}
      </Section>
      <SectionTemplates
        templates={page.templates.map((template) => {
          const color = getTemplateColorFromRemote(template.color)

          return {
            title: template.name,
            description: template.articleDescription,
            imageUrl: template.image.url,
            slug: template.slug.current,
            color: color,
          }
        })}
      />
      <SectionFAQ background="moon-dust-lighter" faq={page.faqs} />
      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
